import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, ShoppingCart, User, Menu, X, Phone, ChevronDown, Video, Cpu, Flame } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import siteConfig from '../../config/siteConfig';

const Header = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { cartItemCount, setCartOpen } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { 
      name: 'Boutique', 
      children: [
        { name: 'Baseline Alarme Sans Fil', path: '/shop', icon: ShieldCheck },
        { name: 'Baseline Vidéoprotection IA', path: '/video', icon: Video },
        { name: 'Baseline Incendie & Inondation', path: '/fire', icon: Flame },
      ]
    },
    { 
      name: 'Pro', 
      children: [
        { name: 'Superior Alarme Grade 2', path: '/superior-alarm-g2' },
        { name: 'Superior Alarme Grade 3 — Sites Sensibles', path: '/superior-alarm-g3' },
        { name: 'Superior Vidéoprotection IA', path: '/superior-video' },
        { name: 'Superior SSI Incendie EN54', path: '/superior-ssi' },
      ]
    },
    { name: 'Domotique', path: '/automation' },
    { name: 'Protection IA', path: '/ai-security', icon: Cpu },
    { name: 'Installation', path: '/installation' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 w-full z-50 border-b border-white/5 bg-black/80 backdrop-blur-xl">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white hover:text-emerald-400 transition-colors"
            data-testid="logo-link"
          >
            <ShieldCheck className="w-7 h-7 text-emerald-400" />
            <span className="font-heading text-xl font-semibold tracking-tight">FGS</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0">
            {navLinks.map((link) => (
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-white/50 hover:text-white transition-colors"
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#111] border-white/10 rounded-xl">
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link 
                          to={child.path}
                          className="cursor-pointer text-white/60 hover:text-white flex items-center gap-2 text-sm"
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {child.icon && <child.icon className="w-4 h-4 text-emerald-400" />}
                          {child.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-[13px] font-medium transition-colors flex items-center gap-1 ${
                    isActive(link.path) 
                      ? 'text-white' 
                      : 'text-white/50 hover:text-white'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1 md:gap-3">
            {/* Phone - Desktop only */}
            <a 
              href={`tel:${siteConfig.contact.phoneFormatted}`}
              className="hidden xl:flex items-center gap-2 text-[13px] text-white/60 hover:text-white transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>{siteConfig.contact.phone}</span>
            </a>

            {/* Cart Button */}
            <Button
              variant="ghost"
              size="icon"
              className="relative text-white/50 hover:text-white hover:bg-white/5"
              onClick={() => setCartOpen(true)}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Button>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-white/50 hover:text-white hover:bg-white/5"
                    data-testid="user-menu-button"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#111] border-white/10 w-56 rounded-xl">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-white/50">{user?.email}</p>
                    {user?.role === 'admin' && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-400 rounded">
                        Admin
                      </span>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-fgs-border" />
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer text-white/50 hover:text-white">
                      Mon compte
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer text-white/50 hover:text-white" onClick={() => {}}>
                      Mes commandes
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator className="bg-fgs-border" />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer text-emerald-400 hover:text-emerald-400">
                          Administration
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-fgs-border" />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-fgs-accent hover:text-fgs-accent"
                    data-testid="logout-button"
                  >
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button 
                  variant="ghost"
                  className="hidden sm:flex text-white/50 hover:text-white text-[13px] hover:bg-white/5"
                  data-testid="login-button"
                >
                  Connexion
                </Button>
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="lg:hidden text-white/50 hover:text-white hover:bg-white/5"
                  data-testid="mobile-menu-button"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0a0a0a] border-white/5 w-80">
                <nav className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    link.children ? (
                      <div key={link.name} className="space-y-2">
                        <span className="text-xs font-medium text-white/60 uppercase tracking-wider">
                          {link.name}
                        </span>
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block pl-4 py-2 text-white/70 hover:text-white transition-colors text-sm"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`py-2 text-base font-medium transition-colors ${
                          isActive(link.path) 
                            ? 'text-white' 
                            : 'text-white/60 hover:text-white'
                        }`}
                      >
                        {link.name}
                      </Link>
                    )
                  ))}
                  
                  {!isAuthenticated && (
                    <Link
                      to="/auth"
                      onClick={() => setMobileMenuOpen(false)}
                      className="mt-4"
                    >
                      <Button className="w-full bg-white text-black hover:bg-white/90 rounded-full font-medium">
                        Connexion
                      </Button>
                    </Link>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
